type Props = {
  className?: string;
  variant?: "default" | "compact" | "mono-light";
  showWordmark?: boolean;
};

export function Logo({
  className = "",
  variant = "default",
  showWordmark = true,
}: Props) {
  const isLight = variant === "mono-light";
  const wordTone = isLight ? "text-white" : "text-ink-900";
  const dotTone = isLight ? "text-amber-300" : "text-amber-500";
  const tldTone = isLight ? "text-white/55" : "text-ink-400";
  const size = variant === "compact" ? 32 : 38;

  return (
    <span className={`inline-flex items-center gap-2.5 leading-none ${className}`}>
      <LogoMark size={size} />
      {showWordmark && (
        <span className="flex items-baseline">
          <span
            className={`font-display font-extrabold tracking-tight ${wordTone}`}
            style={{
              fontSize: variant === "compact" ? 17 : 20,
              letterSpacing: "-0.025em",
            }}
          >
            casinomenu
          </span>
          <span
            aria-hidden
            className={`mx-0.5 text-[10px] font-black ${dotTone}`}
            style={{ transform: "translateY(-2px)" }}
          >
            ●
          </span>
          <span
            className={`font-display font-semibold ${tldTone}`}
            style={{ fontSize: variant === "compact" ? 14 : 16 }}
          >
            se
          </span>
        </span>
      )}
    </span>
  );
}

/**
 * LogoMark — a single, confident geometric "M".
 *
 * The M is drawn as one continuous stroke with rounded joins, sitting on a
 * deep-navy squircle. A thin gold underline anchors the mark and ties the
 * navy ↔ gold palette together. The whole thing reads as a quiet, premium
 * monogram — recognizable at favicon size, dignified at hero size.
 */
export function LogoMark({ size = 38 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      role="img"
      aria-label="CasinoMenu"
      className="shrink-0"
    >
      <defs>
        <linearGradient id="cm-bg" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#1f3a8a" />
          <stop offset="1" stopColor="#0a1240" />
        </linearGradient>
        <linearGradient id="cm-gold" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0" stopColor="#ffd76a" />
          <stop offset="1" stopColor="#f59e0b" />
        </linearGradient>
        <radialGradient id="cm-shine" cx="30%" cy="0%" r="120%">
          <stop offset="0" stopColor="#ffffff" stopOpacity="0.18" />
          <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Squircle */}
      <rect x="0" y="0" width="64" height="64" rx="14" fill="url(#cm-bg)" />
      <rect x="0" y="0" width="64" height="64" rx="14" fill="url(#cm-shine)" />
      {/* Subtle inner border to lift the chip off any background */}
      <rect
        x="0.75"
        y="0.75"
        width="62.5"
        height="62.5"
        rx="13.25"
        fill="none"
        stroke="rgba(255,255,255,0.07)"
        strokeWidth="1.5"
      />

      {/* The M — single continuous stroke with rounded joins */}
      <path
        d="M 16 46 L 16 20 L 32 36 L 48 20 L 48 46"
        fill="none"
        stroke="#ffffff"
        strokeWidth="6.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Gold underline accent — anchors the M, hints at premium / curated */}
      <rect x="20" y="51" width="24" height="2.5" rx="1.25" fill="url(#cm-gold)" />
    </svg>
  );
}
